import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faRobot, faTimes } from "@fortawesome/free-solid-svg-icons";
import { chatCompletion } from "../../data/hf";

const SYSTEM_PROMPT =
	`You are a helpful assistant for Than Kywe. Be concise and professional.
    
    Name: Than Kywe (Ah Kywe)
    Role: Software Engineer / Backend Developer
    Email: thankywe1997@gmail.com
    Phone: +95 9 969 889 147
    
    Socials:
    - GitHub: https://github.com/Than890
    - LinkedIn: https://www.linkedin.com/in/than-kywe/
    
    Education:
    - 2014 – 2019: University of Computer Studies, Meiktila (Myanmar), Bachelor of Computer Science (B.C.Sc)
    
    Skills:
    - Backend Technologies: Node.js, NestJS, Express.js, TypeScript, PHP, Laravel
    - Frontend: React Js, HTML, CSS, Bootstrap, JavaScript, jQuery
    - Databases: MySQL, MongoDB, DynamoDB
    - ERP & Platforms: ERPNext (Frappe), Custom ERP Systems
    - Cloud & DevOps: Linux Server, DigitalOcean, AWS, Server Deployment
    - Integrations & Tools: WhatsApp API, Dialogflow, Microsoft Azure (Form Recognizer / Azure OCR), OpenAI, Payment Gateways (Stripe, Dinger, Sai Sai Pay)
    
    Work Experience:
    - CloudE8 Pte Ltd (June 2024 - Present | Full-time, Remote, Singapore) - Software Developer
      * Customized and extended ERPNext (Frappe) modules to support business-specific workflows.
      * Designed and implemented a booking / appointment system integrated with ERPNext.
      * Stored and managed booking data directly in ERPNext DocTypes, ensuring data consistency and traceability.
      * Integrated Stripe payments with booking workflows and synchronized payment status with ERPNext records.
      * Developed Node.js APIs to connect external services with ERPNext.
      * Implemented Azure OCR (Form Recognizer) to extract invoice data and automate ERPNext invoice creation.
      * Integrated WhatsApp chatbot, Dialogflow, DynamoDB, and OpenAI for intelligent automation.
      * Ensured code quality through reviews, testing, and adherence to best practices.
    - Solinx Technology Co.,Ltd (November 2023 - April 2024 | Full-time, Remote, Yangon, Myanmar) - Senior Backend Developer
      * Developed XH Express, a China-to-Myanmar delivery system using NestJS & Prisma ORM.
      * Designed MySQL database schemas and backend architecture.
      * Built APIs for China Counter App, Myanmar Counter App, User App, and Admin Dashboard.
      * Deployed and maintained services on DigitalOcean Linux servers.
    - EDKA Digital Co.,Ltd (July 2022 - August 2023 | Full-time, Onsite, Mandalay, Myanmar) - Backend Developer
      * Developed a custom ERP system for Aung Thamar Di Gold & Jewellery.
      * Built backend services using Node.js, TypeScript, and MySQL.
      * Delivered APIs supporting mobile and web applications.
      * Managed cloud deployment on AWS Linux servers.
    - Global New Wave Technology Co.,Ltd (March 2020 - June 2022 | Full-time, Hybrid, Yangon, Myanmar) - Backend Web Developer
      * Developed and maintained customized web applications.
      * Built APIs using Node.js, TypeScript, GraphQL.
      * Worked with MySQL, MongoDB, and Linux servers (DigitalOcean).
      * Collaborated with mobile and frontend teams to deliver stable APIs.
    - Studio AMK Co.,Ltd (May 2019 - November 2019 | Internship, Onsite, Yangon, Myanmar) - Internship
      * Gained hands-on experience with Node.js backend development.
      * Assisted in API development and system maintenance.
      
    Projects:
    - Read Fam: Social Reading & Creator Monetization Platform (Nest.js, Next.js)
      * Built a dual-currency wallet system with Stripe integrations for secure top-ups and automated creator payouts.
      * Built a real-time virtual reward distribution feature ("Flower Sharing") allowing creators to share virtual currency with followers or public users via notification-triggered claims.
      * Engineered APIs to handle multi-chapter webtoon publishing, user comments/reviews.
      * Developed a responsive admin panel to manage content creators, review user KYC documents, track Stripe financial transactions, and moderate stories/chapters.
      * Delivered APIs for mobile apps.
    - PREMIER STRUCTURE (Frappe, ERPNext, Node.js)
      * Customized HR module to support both Malaysia and Singapore payroll requirements.
      * Integrated WhatsApp Chatbot to allow employees to access personal information, such as Salary Slips and Leave Balances.
    - OBBA (Frappe, ERPNext, Node.js)
      * Customized the Frappe ERP according to specific requirements.
      * Integrated WhatsApp Chatbot to enable the upload of Purchase Invoices and POS Daily Sales Orders from Obba's BBQ outlets.
      * Used OpenAI Embeddings to predict and prevent duplicate creation of Items and Suppliers.
      * Used Azure OCR to extract structured invoice data and store it in Frappe ERP (ERPNext).
    - XH EXPRESS: China to Myanmar Delivery System (Nest.js, MySQL, Prisma ORM)
      * Designed backend architecture and database schema.
      * Developed APIs for logistics operations and multi-platform apps.
    - POSEM Multi POS System (Node.js, Express.js, MongoDB)
      * Implementation of API for Mobile App and developed web portal.
      * Payment Integration with Dinger.
    - Hyper POS: Free Point of Sale System.
    - Token Doctor Queue Management System (Node.js, Nest.js, MySQL)
      * Implemented new backend architecture with Nest.js.
      * Implementation of API for Mobile App and Web portal.
      * Payment Integration with Dinger.
    - Aung Thamar Di - ERP (Node.js, Express.js, TypeScript)
      * Implementation of database design using MySQL with Project Manager.
      * Implemented new backend architecture with TypeScript.
      * Implementation of API for IT Module, Admin Module, Gold Smith, Gold Sale, and HR Module.
      * Implementation of API for HR App, Gold Sale App.
    - Gold & Jewellery POS Systems (NewTaiSin, LanYin) (Node.js, Express.js, TypeScript)
      * Designed databases and backend architecture.
      * Developed modules for orders, stock, suppliers, and sales.
    - Ecommerce Platforms (PTB GO, MyHealthCare Pharmacy) (Node.js, Express.js)
      * Built order, delivery, supplier, and reporting modules.
      * Integrated payment gateways and real-time delivery tracking.
      * Delivered APIs for mobile apps and websites.`;

const ChatWidget = () => {
	const [open, setOpen] = useState(false);
	const [messages, setMessages] = useState([
		{ role: "system", content: SYSTEM_PROMPT },
		{ role: "assistant", content: "Hello! I'm your portfolio assistant. Ask me anything about Than Kywe's background, skills, or experience!" },
	]);
	const [input, setInput] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isDarkMode, setIsDarkMode] = useState(false);
	const listRef = useRef(null);

	// Check user's preferred theme from localStorage or system preference
	useEffect(() => {
		// Initialize dark mode state
		const initializeDarkMode = () => {
			const savedTheme = localStorage.getItem('theme');
			const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
			const isDark = savedTheme === 'dark' || (!savedTheme && prefersDark);
			setIsDarkMode(isDark);
		};

		// Initialize on component mount
		initializeDarkMode();

		// Listen for changes in system theme preference
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleSystemThemeChange = () => initializeDarkMode();
		mediaQuery.addEventListener('change', handleSystemThemeChange);

		// Clean up event listener on unmount
		return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
	}, []);

	// Listen for changes to localStorage theme from other components
	useEffect(() => {
		const handleStorageChange = (e) => {
			if (e.key === 'theme') {
				const newTheme = e.newValue;
				setIsDarkMode(newTheme === 'dark');
			}
		};

		// Add event listener for storage changes
		window.addEventListener('storage', handleStorageChange);

		// Clean up event listener on unmount
		return () => window.removeEventListener('storage', handleStorageChange);
	}, []);

	// Also check for direct attribute changes on documentElement
	useEffect(() => {
		const checkThemeAttribute = () => {
			// Properly check if data-theme is set to 'dark'
			const hasDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
			const savedTheme = localStorage.getItem('theme');
			
			// If there's a discrepancy between attribute and localStorage,
			// update localStorage to match the attribute
			if (hasDarkTheme !== (savedTheme === 'dark')) {
				localStorage.setItem('theme', hasDarkTheme ? 'dark' : 'light');
				setIsDarkMode(hasDarkTheme);
			}
		};

		// Create a mutation observer to watch for attribute changes on documentElement
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.attributeName === 'data-theme') {
					checkThemeAttribute();
				}
			});
		});

		// Observe changes to documentElement attributes
		observer.observe(document.documentElement, {
			attributes: true
		});

		// Initial check
		checkThemeAttribute();

		// Clean up observer on unmount
		return () => observer.disconnect();
	}, []);

	const visible = useMemo(
		() => messages.filter((m) => m.role !== "system"),
		[messages]
	);

	const scrollToBottom = useCallback(() => {
		if (listRef.current) {
			listRef.current.scrollTop = listRef.current.scrollHeight;
		}
	}, []);

	const onSend = useCallback(async () => {
		const trimmed = input.trim();
		if (!trimmed || isLoading) return;
		const next = [...messages, { role: "user", content: trimmed }];
		setMessages(next);
		setInput("");
		setIsLoading(true);
		try {
			const reply = await chatCompletion(next);
			setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
			setTimeout(scrollToBottom, 0);
		} catch (e) {
			setMessages((prev) => [
				...prev,
				{
					role: "assistant",
					content: `Error: ${e.message}. Please try again.`,
				},
			]);
		} finally {
			setIsLoading(false);
		}
	}, [input, isLoading, messages, scrollToBottom]);

	const onKeyDown = useCallback(
		(e) => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				onSend();
			}
		},
		[onSend]
	);

	const handleSuggestion = (suggestion) => {
		setInput(suggestion);
		onSend();
	};

	return (
		<div className="chatbot-root">
			{open ? (
				<div className="chatbot-panel">
					<div className="chatbot-header">
						<div className="status">
							<div className="status-icon">
								<FontAwesomeIcon icon={faRobot} size="lg" style={{color:"var(--white)"}} />
							</div>
							<span style={{color:"var(--success-color)"}}>Online</span>
						</div>
						<div className="actions">
							<button 
								onClick={() => {
									setMessages([{ role: "system", content: SYSTEM_PROMPT }, { role: "assistant", content: "Hello! I'm your portfolio assistant. Ask me anything about Than Kywe's background, skills, or experience!" }]);
								}}
								style={{
									// Using text-inverse variable to match header text color in both modes
									color: 'var(--text-inverse)',
									backgroundColor: 'transparent',
									border: 'none',
									padding: '6px 12px',
									borderRadius: '4px',
									cursor: 'pointer',
									transition: 'all 0.3s ease'
								}}
								onMouseEnter={(e) => e.target.style.backgroundColor = isDarkMode ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.15)'}
								onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
							>
								Clear
							</button>
							<button 
								onClick={() => setOpen(false)}
								style={{
									// Using text-inverse variable to match header text color in both modes
									color: 'var(--text-inverse)',
									backgroundColor: 'transparent',
									border: 'none',
									padding: '6px 12px',
									marginLeft: '8px',
									borderRadius: '4px',
									cursor: 'pointer',
									transition: 'all 0.3s ease'
								}}
								onMouseEnter={(e) => e.target.style.backgroundColor = isDarkMode ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.15)'}
								onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
							>
								<FontAwesomeIcon icon={faTimes} />
							</button>
						</div>
					</div>
					<div className="chatbot-messages" ref={listRef}>
						{visible.length === 0 && (
							<div className="chatbot-empty">How may I assist you today?</div>
						)}
						{visible.map((m, i) => (
							<div
								key={i}
								className={m.role === "assistant" ? "chatbot-msg assistant" : "chatbot-msg user"}
							>
								<div className="chatbot-bubble">
									{m.content}
									<div className="time">
										{i === 1 ? "06:40 PM" : new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Yangon' })}
									</div>
								</div>
							</div>
						))}
						{isLoading && (
							<div className="chatbot-msg assistant">
								<div className="chatbot-bubble loading">Processing...</div>
							</div>
						)}
					</div>
					<div className="chatbot-input-row">
						<input
							className="chatbot-input"
							value={input}
							placeholder="Type your message..."
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={onKeyDown}
						/>
						<button
							className="chatbot-send"
							onClick={onSend}
							disabled={isLoading || !input.trim()}
							aria-label="Send message"
						>
								<FontAwesomeIcon icon={faPaperPlane} size="sm" />
						</button>
					</div>
					<div className="chatbot-suggestions">
						<span>Try asking:</span>
						<button onClick={() => handleSuggestion("Skills")}>Skills</button>
						<button onClick={() => handleSuggestion("Experience")}>Experience</button>
						<button onClick={() => handleSuggestion("Projects")}>Projects</button>
					</div>
				</div>
			) : (
				<button
					className="chatbot-toggle"
					onClick={() => setOpen(true)}
					aria-label="Open chat"
				>
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
						</svg>
				</button>
			)}
		</div>
	);
};

export default ChatWidget;


